import { Container, Graphics } from "pixi.js";
import type { PlotData } from "../api/types";
import { IsoProjection } from "../core/projection";

type PlotEntry = Container & { plotData?: PlotData; selectionOutline?: Graphics };

export class PlotLayer extends Container {
  private readonly entries = new Map<number, PlotEntry>();
  private readonly plots = new Map<number, PlotData>();
  private selectionHandler: ((plot: PlotData) => void) | null = null;
  private selectedId: number | null = null;
  private marketMode = false;

  constructor(
    plots: PlotData[],
    private readonly projection: IsoProjection,
    private readonly extent = 28,
    private readonly editorMode = false,
  ) {
    super();
    this.label = "plots";
    this.sortableChildren = true;
    this.replacePlots(plots);
  }

  onSelect(callback: (plot: PlotData) => void): void {
    this.selectionHandler = callback;
  }

  setMarketMode(active: boolean): void {
    this.marketMode = active;
    this.applyVisibility();
  }

  isMarketMode(): boolean {
    return this.marketMode;
  }

  setSelected(id: number | null): void {
    this.selectedId = id;
    this.entries.forEach((entry, plotId) => {
      const shape = entry.children[0];
      if (shape) shape.alpha = plotId === id ? 1 : 0.96;
      // Editor plot vertices are projected in world space: scaling around (0, 0)
      // shifts the parcel away from its saved location. Highlight without transforms.
      entry.scale.set(1);
      if (entry.selectionOutline) entry.selectionOutline.visible = plotId === id;
    });
  }

  replacePlots(plots: PlotData[]): void {
    this.plots.clear();
    for (const plot of plots) this.plots.set(plot.id, { ...plot });

    for (const child of this.removeChildren()) child.destroy({ children: true });
    this.entries.clear();

    for (const plot of this.plots.values()) {
      if (Math.abs(plot.gridX) > this.extent || Math.abs(plot.gridY) > this.extent) {
        continue;
      }
      if (
        !this.editorMode &&
        !plot.forSale &&
        !plot.ownedByViewer &&
        !plot.listing?.isListed
      ) continue;
      this.addPlot(plot);
    }
    this.applyVisibility();
    this.setSelected(this.selectedId);
  }

  upsertPlot(plot: PlotData): void {
    this.plots.set(plot.id, { ...plot });
    const existing = this.entries.get(plot.id);
    if (existing) {
      existing.destroy({ children: true });
      this.entries.delete(plot.id);
    }
    if (Math.abs(plot.gridX) <= this.extent && Math.abs(plot.gridY) <= this.extent) {
      if (
        this.editorMode ||
        plot.forSale ||
        plot.ownedByViewer ||
        plot.listing?.isListed
      ) {
        this.addPlot(plot);
      }
    }
    this.applyVisibility();
    this.setSelected(this.selectedId);
  }

  removePlot(id: number): void {
    this.plots.delete(id);
    const entry = this.entries.get(id);
    if (entry) {
      this.removeChild(entry);
      entry.destroy({ children: true });
      this.entries.delete(id);
    }
    if (this.selectedId === id) this.selectedId = null;
  }

  getPlot(id: number): PlotData | null {
    const plot = this.plots.get(id);
    return plot ? { ...plot } : null;
  }

  private isMarketPlot(plot: PlotData): boolean {
    return Boolean(plot.forSale || plot.listing?.isListed);
  }

  private applyVisibility(): void {
    this.entries.forEach((entry) => {
      const plot = entry.plotData;
      if (!plot) return;
      entry.visible = this.editorMode ||
        plot.ownedByViewer ||
        (this.marketMode && this.isMarketPlot(plot));
      entry.eventMode = entry.visible ? "static" : "none";
    });
  }

  private addPlot(plot: PlotData): void {
    const entry = new Container() as PlotEntry;
    entry.plotData = plot;
    entry.label = `plot:${plot.id}`;
    entry.eventMode = "static";
    entry.cursor = this.editorMode || plot.forSale || plot.listing?.isListed ||
      plot.ownedByViewer ? "pointer" : "default";
    entry.on("pointertap", () => {
      if (entry.plotData) this.selectionHandler?.(entry.plotData);
    });

    // Public sale overlays must match the exact persisted parcel envelope.
    // Never clamp to a decorative 3.2-unit marker: it makes purchasable
    // land look smaller than the administrator's defined lot.
    const visualWidth = plot.width;
    const visualDepth = plot.depth;
    const x0 = plot.gridX - visualWidth / 2;
    const y0 = plot.gridY - visualDepth / 2;
    const x1 = x0 + visualWidth;
    const y1 = y0 + visualDepth;

    const a = this.projection.worldToScreen(x0, y0);
    const b = this.projection.worldToScreen(x1, y0);
    const c = this.projection.worldToScreen(x1, y1);
    const d = this.projection.worldToScreen(x0, y1);

    const isListed = Boolean(plot.listing?.isListed);
    const fill = this.editorMode
      ? plot.ownerName
        ? 0x8b5cf6
        : plot.forSale
          ? 0xf2c94c
          : 0x38bdf8
      : plot.ownedByViewer
        ? 0x60a5fa
        : isListed
          ? 0xf59e0b
          : 0xf2c94c;

    const alpha = this.editorMode
      ? 0.15
      : plot.ownedByViewer
        ? 0.12
        : 0.11;
    const borderAlpha = this.editorMode ? 0.95 : 0.78;

    const shape = new Graphics()
      .poly([a.x, a.y, b.x, b.y, c.x, c.y, d.x, d.y])
      .fill({ color: fill, alpha })
      .stroke({ color: fill, width: this.editorMode ? 2 : 1.4, alpha: borderAlpha });

    entry.addChild(shape);
    if (this.editorMode) {
      const outline = new Graphics()
        .poly([a.x, a.y, b.x, b.y, c.x, c.y, d.x, d.y])
        .fill({ color: 0xf472b6, alpha: 0.22 })
        .stroke({ color: 0xf472b6, width: 3, alpha: 1 });
      outline.visible = false;
      outline.eventMode = "none";
      entry.selectionOutline = outline;
      entry.addChild(outline);
    }
    entry.zIndex = plot.gridX + plot.gridY - 5000;
    this.entries.set(plot.id, entry);
    this.addChild(entry);
  }
}
